import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { FiAlertCircle, FiCheck, FiCheckCircle, FiChevronDown, FiChevronUp, FiCircle, FiDownload, FiFileText, FiGrid, FiRotateCcw, FiTrash2, FiX } from 'react-icons/fi';
import { TOKENS } from '@/constants/designTokens';
import {
	countReportExports,
	describeReportExport,
	isReportExportActive,
	reportExportBadge,
	type ReportExportCounts,
	type ReportExportStepState,
	type ReportExportTone
} from '@/helpers/report-export-view';
import type { ReportExportJob } from '../../shared/schemas/report-export/report-export.schema';

// MF-1143: painel das exportações assíncronas, no canto inferior direito, inspirado no painel de envios do Drive.
// Três estados: expandido (a lista), minimizado (uma barra com os contadores) e oculto (só um ícone no canto).

export type ReportExportsPanelView = 'expanded' | 'compact' | 'hidden';

const SUCCESS_COLOR = '#1a7f4b';
const DANGER_COLOR = '#c2292e';

const TONE_COLORS = new Map<ReportExportTone, string>([
	['info', 'var(--primary-color)'],
	['success', SUCCESS_COLOR],
	['warning', '#a15c00'],
	['danger', DANGER_COLOR],
	['muted', TOKENS.textSubtle]
]);

const CORNER_GAP = 8;
const CONTROLS = 'button, a[href], input, select, textarea, [role="button"]';
// Controle mais alto que isto é um bloco clicável inteiro (um card), não um botão: não vale subir por ele.
const MAX_CONTROL_HEIGHT = 80;
const AVOID_CHECK_MS = 1000;

/** Controle preso na tela (dentro de um `fixed` ou `sticky`): rolar não o tira de baixo do painel. */
const isPinned = (element: Element): boolean => {
	for (let node: Element | null = element; node && node !== document.body; node = node.parentElement) {
		const { position } = getComputedStyle(node);
		if (position === 'fixed' || position === 'sticky') return true;
	}
	return false;
};

/**
 * Posição vertical do minimizado: no canto, ou acima do controle preso na tela (botão, link, campo dentro de um
 * `fixed`/`sticky`) que ele cobriria ali, como o "Salvar alterações" do rodapé da ficha do paciente. Um rodapé
 * `sticky` muda de altura com a rolagem, então uma distância fixa não serve. Conteúdo que rola (as ações de uma
 * tabela) não conta: a pessoa rola e tira de baixo, e o painel não fica subindo linha a linha. Amostra pontos da
 * área com `elementsFromPoint` (barato, sem percorrer a página), na rolagem, no redimensionamento e a cada
 * segundo (troca de tela, rodapé que aparece).
 */
function useAvoidControls(ref: React.RefObject<HTMLElement | null>): number {
	const [bottom, setBottom] = useState(CORNER_GAP);

	useEffect(() => {
		let frame = 0;

		const controlsUnder = (left: number, top: number, width: number, height: number): DOMRect[] => {
			const found = new Set<Element>();
			for (const fx of [0.05, 0.35, 0.65, 0.95]) {
				for (const fy of [0.1, 0.5, 0.9]) {
					for (const element of document.elementsFromPoint(left + width * fx, top + height * fy)) {
						if (ref.current?.contains(element)) continue;
						const control = element.closest(CONTROLS);
						if (control && !found.has(control) && control.getBoundingClientRect().height <= MAX_CONTROL_HEIGHT && isPinned(control)) found.add(control);
					}
				}
			}
			return [...found].map(control => control.getBoundingClientRect());
		};

		const measure = () => {
			frame = 0;
			const panel = ref.current;
			if (!panel) return;
			const { width, height } = panel.getBoundingClientRect();
			const left = document.documentElement.clientWidth - CORNER_GAP - width;

			let next = CORNER_GAP;
			// Sobe acima do controle coberto e confere de novo (pode haver outro logo acima).
			for (let attempt = 0; attempt < 3; attempt++) {
				const top = window.innerHeight - next - height;
				const covered = controlsUnder(left, top, width, height);
				if (covered.length === 0) break;
				next = window.innerHeight - Math.min(...covered.map(rect => rect.top)) + CORNER_GAP;
			}
			setBottom(current => (current === next ? current : next));
		};

		const schedule = () => {
			if (!frame) frame = requestAnimationFrame(measure);
		};

		schedule();
		window.addEventListener('scroll', schedule, { capture: true, passive: true });
		window.addEventListener('resize', schedule);
		const timer = setInterval(schedule, AVOID_CHECK_MS);
		return () => {
			cancelAnimationFrame(frame);
			window.removeEventListener('scroll', schedule, { capture: true });
			window.removeEventListener('resize', schedule);
			clearInterval(timer);
		};
	}, [ref]);

	return bottom;
}

const Panel = styled.section`
	position: fixed;
	right: 20px;
	bottom: 20px;
	z-index: 9997;
	width: 360px;
	max-width: calc(100vw - 32px);
	background: ${TOKENS.surface};
	border: 1px solid ${TOKENS.border};
	border-radius: ${TOKENS.radiusCard};
	box-shadow: ${TOKENS.shadowPopover};
	display: flex;
	flex-direction: column;
	overflow: hidden;
	/* Rolar sobre o painel (cabeçalho, minimizado) não rola a página de trás */
	overscroll-behavior: contain;

	@media (max-width: 575px) {
		right: 16px;
		bottom: 16px;
	}
`;

// Minimizado: no canto, com a largura só do conteúdo; sobe sozinho quando cobriria um controle da tela
// (useAvoidControls). O oculto, só um ícone, fica sempre no canto.
const Compact = styled.section`
	position: fixed;
	right: ${CORNER_GAP}px;
	transition: bottom 150ms ease;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
	z-index: 9997;
	display: flex;
	align-items: center;
	gap: 2px;
	max-width: calc(100vw - 16px);
	padding: 4px 4px 4px 4px;
	background: ${TOKENS.surface};
	border: 1px solid ${TOKENS.border};
	border-radius: 999px;
	box-shadow: ${TOKENS.shadowPopover};
	overscroll-behavior: contain;
`;

const CompactSummary = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 10px;
	height: 30px;
	padding: 0 8px 0 12px;
	border: 0;
	border-radius: 999px;
	background: transparent;
	font-size: 13px;
	font-weight: 600;
	color: ${TOKENS.textStrong};
	cursor: pointer;
	white-space: nowrap;

	&:hover {
		background: ${TOKENS.track};
	}
`;

const Count = styled.span<{ $color: string }>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	font-weight: 600;
	color: ${({ $color }) => $color};
`;

const Launcher = styled.button`
	position: fixed;
	right: 8px;
	bottom: 8px;
	z-index: 9997;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	border: 1px solid ${TOKENS.border};
	border-radius: 50%;
	background: ${TOKENS.surface};
	box-shadow: ${TOKENS.shadowPopover};
	cursor: pointer;
	overscroll-behavior: contain;

	&:hover {
		background: ${TOKENS.track};
	}
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 12px 10px 16px;
	border-bottom: 1px solid ${TOKENS.divider};
	background: ${TOKENS.surface};
`;

const HeaderTitle = styled.div`
	flex: 1;
	min-width: 0;
	font-size: 14px;
	font-weight: 600;
	color: ${TOKENS.textStrong};
`;

const HeaderSummary = styled.span`
	display: block;
	font-size: 12px;
	font-weight: 400;
	color: ${TOKENS.textSubtle};
`;

const IconButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	border: 0;
	border-radius: ${TOKENS.radiusControl};
	background: transparent;
	color: ${TOKENS.textMuted};
	cursor: pointer;

	&:hover {
		background: ${TOKENS.track};
	}
`;

const List = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	max-height: min(60vh, 480px);
	overflow-y: auto;
	/* No fim (ou no topo) da lista, a rolagem não passa para a página de trás */
	overscroll-behavior: contain;
`;

// Saída suave do item removido: esmaece e encolhe, para a lista não "pular".
const leave = keyframes`
	from {
		opacity: 1;
		max-height: 400px;
	}
	to {
		opacity: 0;
		max-height: 0;
		padding-top: 0;
		padding-bottom: 0;
	}
`;

/** Duração da saída; o contexto espera este tempo antes de tirar o item da lista. */
export const REPORT_EXPORT_LEAVE_MS = 250;

const Item = styled.li<{ $leaving: boolean }>`
	padding: 12px 16px;
	border-bottom: 1px solid ${TOKENS.divider};

	&:last-of-type {
		border-bottom: 0;
	}

	${({ $leaving }) =>
		$leaving &&
		css`
			overflow: hidden;
			pointer-events: none;
			animation: ${leave} ${REPORT_EXPORT_LEAVE_MS}ms ease forwards;

			@media (prefers-reduced-motion: reduce) {
				animation-duration: 1ms;
			}
		`}
`;

const ItemHeader = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 10px;
`;

const ItemTitle = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: ${TOKENS.text};
	overflow-wrap: anywhere;
`;

const Status = styled.div<{ $tone: ReportExportTone }>`
	font-size: 12px;
	color: ${({ $tone }) => TONE_COLORS.get($tone)};
	margin-top: 2px;
`;

const Steps = styled.ol`
	list-style: none;
	margin: 8px 0 0 26px;
	padding: 0;
`;

const STEP_COLORS = new Map<ReportExportStepState, string>([
	['done', SUCCESS_COLOR],
	['current', TOKENS.textStrong],
	['pending', TOKENS.textPlaceholder],
	['failed', DANGER_COLOR]
]);

const Step = styled.li<{ $state: ReportExportStepState }>`
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
	line-height: 20px;
	color: ${({ $state }) => STEP_COLORS.get($state)};
	font-weight: ${({ $state }) => ($state === 'current' ? 600 : 400)};
`;

const Note = styled.div`
	font-size: 12px;
	color: ${TOKENS.textSubtle};
	margin: 6px 0 0 26px;
	overflow-wrap: anywhere;
`;

const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin: 8px 0 0 26px;
`;

const DownloadedPrompt = styled.div`
	margin: 8px 0 0 26px;
	padding: 8px 10px;
	border-radius: ${TOKENS.radiusControl};
	background: ${TOKENS.track};
	font-size: 12px;
	color: ${TOKENS.text};
`;

const Spinner = ({ size = 12 }: { size?: number }) => (
	<span className='spinner-border spinner-border-sm' style={{ width: size, height: size, borderWidth: 2 }} aria-hidden='true' />
);

const StepIcon = ({ state }: { state: ReportExportStepState }) => {
	if (state === 'done') return <FiCheck size={13} aria-hidden='true' />;
	if (state === 'failed') return <FiX size={13} aria-hidden='true' />;
	if (state === 'current') return <Spinner />;
	return <FiCircle size={11} aria-hidden='true' />;
};

const STEP_STATE_LABELS: Record<ReportExportStepState, string> = {
	done: 'concluída',
	current: 'em andamento',
	pending: 'aguardando',
	failed: 'falhou'
};

/** Resumo dos contadores: no cabeçalho do expandido e, com o prefixo, no hover do minimizado e do ícone. */
const summaryOf = ({ active, ready, failed }: ReportExportCounts): string => {
	const parts = [];
	if (active > 0) parts.push(`${active} em andamento`);
	if (ready > 0) parts.push(`${ready} ${ready === 1 ? 'pronta' : 'prontas'}`);
	if (failed > 0) parts.push(`${failed} com erro`);
	return parts.join(', ');
};

const countsLabel = (counts: ReportExportCounts): string => {
	const summary = summaryOf(counts);
	return summary ? `Exportações: ${summary}` : 'Exportações';
};

const HIDE_LABEL = 'Ocultar (fica só um ícone no canto da tela)';

const CompactBar = ({ counts, label, onChangeView }: { counts: ReportExportCounts; label: string; onChangeView: (view: ReportExportsPanelView) => void }) => {
	const ref = useRef<HTMLElement>(null);
	const bottom = useAvoidControls(ref);

	return (
		<Compact ref={ref} style={{ bottom }} data-cy='report-exports-panel' aria-label='Exportações de relatórios'>
			<CompactSummary type='button' data-cy='report-exports-compact' onClick={() => onChangeView('expanded')} title={`${label}. Clique para expandir.`} aria-label={`${label}. Expandir painel`}>
				Exportações
				{counts.active > 0 && (
					<Count $color='var(--primary-color)'>
						<Spinner />
						{counts.active}
					</Count>
				)}
				{counts.ready > 0 && (
					<Count $color={SUCCESS_COLOR}>
						<FiCheckCircle size={14} aria-hidden='true' />
						{counts.ready}
					</Count>
				)}
				{counts.failed > 0 && (
					<Count $color={DANGER_COLOR}>
						<FiAlertCircle size={14} aria-hidden='true' />
						{counts.failed}
					</Count>
				)}
			</CompactSummary>
			<IconButton type='button' data-cy='report-exports-toggle' onClick={() => onChangeView('expanded')} title='Expandir' aria-label='Expandir painel de exportações' aria-expanded={false}>
				<FiChevronUp size={18} />
			</IconButton>
			<IconButton type='button' data-cy='report-exports-close' onClick={() => onChangeView('hidden')} title={HIDE_LABEL} aria-label='Ocultar painel de exportações'>
				<FiX size={18} />
			</IconButton>
		</Compact>
	);
};

interface ReportExportsPanelProps {
	jobs: ReportExportJob[];
	view: ReportExportsPanelView;
	busyIds: number[];
	leavingIds: number[];
	downloadedIds: number[];
	onChangeView: (view: ReportExportsPanelView) => void;
	onCancel: (job: ReportExportJob) => void;
	onRetry: (job: ReportExportJob) => void;
	onDownload: (job: ReportExportJob) => void;
	onDismiss: (job: ReportExportJob) => void;
	onKeep: (job: ReportExportJob) => void;
}

export const ReportExportsPanel = ({ jobs, view, busyIds, leavingIds, downloadedIds, onChangeView, onCancel, onRetry, onDownload, onDismiss, onKeep }: ReportExportsPanelProps) => {
	const counts = countReportExports(jobs);
	const label = countsLabel(counts);

	if (view === 'hidden') {
		const badge = reportExportBadge(counts);
		if (!badge) return null;
		return (
			<Launcher type='button' data-cy='report-exports-launcher' onClick={() => onChangeView('expanded')} title={`${label}. Clique para abrir.`} aria-label={`${label}. Abrir painel de exportações`}>
				{badge === 'failed' && <FiAlertCircle size={18} color={DANGER_COLOR} aria-hidden='true' />}
				{badge === 'ready' && <FiCheckCircle size={18} color={SUCCESS_COLOR} aria-hidden='true' />}
				{badge === 'active' && <Spinner size={16} />}
			</Launcher>
		);
	}

	if (view === 'compact') return <CompactBar counts={counts} label={label} onChangeView={onChangeView} />;

	return (
		<Panel data-cy='report-exports-panel' aria-label='Exportações de relatórios'>
			<Header>
				<HeaderTitle>
					Exportações
					<HeaderSummary aria-live='polite'>{summaryOf(counts) || `${jobs.length} ${jobs.length === 1 ? 'concluída' : 'concluídas'}`}</HeaderSummary>
				</HeaderTitle>
				<IconButton type='button' data-cy='report-exports-toggle' onClick={() => onChangeView('compact')} title='Minimizar' aria-label='Minimizar painel de exportações' aria-expanded={true}>
					<FiChevronDown size={18} />
				</IconButton>
				<IconButton type='button' data-cy='report-exports-close' onClick={() => onChangeView('hidden')} title={HIDE_LABEL} aria-label='Ocultar painel de exportações'>
					<FiX size={18} />
				</IconButton>
			</Header>

			<List>
				{jobs.map(job => {
					const view = describeReportExport(job);
					const busy = busyIds.includes(job.id);
					const canDismiss = !isReportExportActive(job);
					const askAfterDownload = canDismiss && downloadedIds.includes(job.id);
					return (
						<Item key={job.id} data-cy='report-export-item' $leaving={leavingIds.includes(job.id)}>
							<ItemHeader>
								<span style={{ color: TOKENS.textMuted, marginTop: 1 }} aria-hidden='true'>
									{job.format === 'pdf' ? <FiFileText size={16} /> : <FiGrid size={16} />}
								</span>
								<div style={{ minWidth: 0 }}>
									<ItemTitle>{view.title}</ItemTitle>
									<Status $tone={view.tone}>{view.statusLabel}</Status>
								</div>
							</ItemHeader>

							{view.steps.length > 0 && (
								<Steps aria-label='Etapas da exportação'>
									{view.steps.map(step => (
										<Step key={step.key} $state={step.state}>
											<StepIcon state={step.state} />
											<span>
												{step.label}
												<span className='sr-only'> ({STEP_STATE_LABELS[step.state]})</span>
											</span>
										</Step>
									))}
								</Steps>
							)}

							{view.detail && <Note>{view.detail}</Note>}
							{view.availability && <Note>{view.availability}</Note>}
							{view.dataReference && <Note>{view.dataReference}. O arquivo reflete os dados desse momento.</Note>}

							{(view.canDownload || view.canCancel || view.canRetry || (canDismiss && !askAfterDownload)) && (
								<Actions>
									{view.canDownload && (
										<button type='button' className='btn btn-sm btn-primary' data-cy='report-export-download' disabled={busy} onClick={() => onDownload(job)}>
											<FiDownload size={13} className='mr-1' aria-hidden='true' />
											{askAfterDownload ? 'Baixar de novo' : 'Baixar'}
										</button>
									)}
									{view.canRetry && (
										<button type='button' className='btn btn-sm btn-outline-secondary' data-cy='report-export-retry' disabled={busy} onClick={() => onRetry(job)}>
											<FiRotateCcw size={13} className='mr-1' aria-hidden='true' />
											Gerar novamente
										</button>
									)}
									{view.canCancel && (
										<button type='button' className='btn btn-sm btn-outline-secondary' data-cy='report-export-cancel' disabled={busy} onClick={() => onCancel(job)}>
											Cancelar
										</button>
									)}
									{canDismiss && !askAfterDownload && (
										<button
											type='button'
											className='btn btn-sm btn-outline-secondary'
											data-cy='report-export-dismiss'
											disabled={busy}
											onClick={() => onDismiss(job)}
											title='Remover da lista (o arquivo é apagado)'>
											<FiTrash2 size={13} className='mr-1' aria-hidden='true' />
											Remover da lista
										</button>
									)}
								</Actions>
							)}

							{askAfterDownload && (
								<DownloadedPrompt data-cy='report-export-downloaded-prompt'>
									Já baixou o arquivo? Pode remover da lista.
									<Actions style={{ margin: '6px 0 0' }}>
										<button type='button' className='btn btn-sm btn-primary' data-cy='report-export-dismiss' disabled={busy} onClick={() => onDismiss(job)} title='Remover da lista (o arquivo é apagado)'>
											<FiTrash2 size={13} className='mr-1' aria-hidden='true' />
											Remover da lista
										</button>
										<button type='button' className='btn btn-sm btn-outline-secondary' data-cy='report-export-keep' disabled={busy} onClick={() => onKeep(job)} title='Continuar mostrando na lista'>
											Manter
										</button>
									</Actions>
								</DownloadedPrompt>
							)}
						</Item>
					);
				})}
			</List>
		</Panel>
	);
};
